import React, { useState, useMemo, useRef } from "react";
import { Switch, Typography, Box, Button, TextField } from "@material-ui/core";
import dayjs from "dayjs";
import { useDebounceFn } from "ahooks";
import { useTranslation } from "@/langs/useTranslation";
import { ReactRender } from "@/components/ReactRender";
import DataTable from "@/components/DataTable";
import AddAppModal from "../AppConfig/AddAppModal";
import ConfigSwitch from "../../AndroidConfig/common/ConfigSwitch";
import UploadWindowsAppDialog from "@/containers/WindowsApp/UploadWindowsAppDialog";

const PackageIdEditText = ({
  value,
  onChange,
  readOnly,
  rowNumber,
  lastFoused
}) => {
  const { run: handleChange } = useDebounceFn(
    (value, type, index) => {
      onChange({
        target: {
          name: `applicationBlacklist.applications[${index}].${type}`,
          value
        }
      });
    },
    { wait: 800 }
  );

  if (readOnly) {
    return <>{value}</>;
  }

  return (
    <TextField
      size="small"
      variant="outlined"
      defaultValue={value}
      autoFocus={rowNumber === lastFoused.current}
      onFocus={() => {
        lastFoused.current = rowNumber;
      }}
      onChange={e => {
        handleChange(e.target.value, "process_name", rowNumber);
      }}
      style={{ width: 300 }}
    />
  );
};

const AppBlockList = ({ values, onChange, readOnly, setFieldValue }) => {
  const { t } = useTranslation();
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const valuesRef = useRef();
  valuesRef.current = values;

  const lastFoused = useRef(null);

  const columns = [
    {
      data: "name",
      title: t({ defaultMessage: "程序名称" }),
      orderable: false,
      createdCell: (td, data, row, rowNumber) => {
        ReactRender(
          <>
            {!data ? (
              <PackageIdEditText
                lastFoused={lastFoused}
                value={row.process_name}
                onChange={onChange}
                readOnly={readOnly}
                rowNumber={rowNumber}
              />
            ) : (
              data
            )}
          </>,
          td
        );
      }
    },
    {
      data: "version",
      title: t({ defaultMessage: "版本" }),
      orderable: false
    },
    {
      data: "upload_at",
      title: t({ defaultMessage: "上传时间" }),
      orderable: false,
      render: data => {
        if (!data) return "";
        return dayjs(data).format("YYYY-MM-DD HH:mm:ss");
      }
    },
    {
      data: "size",
      title: t({ defaultMessage: "大小" }),
      className: "center",
      orderable: false,
      render: data => {
        if (!data) return "";
        return `${(data / (1024 * 1024)).toFixed(2)} MB`;
      }
    },
    {
      data: null,
      title: t({ defaultMessage: "操作" }),
      className: "center nowrap",
      searchable: false,
      orderable: false,
      width: 80,
      createdCell: (td, data, row, rowNumber) => {
        ReactRender(
          <Button
            size="small"
            color="primary"
            disabled={readOnly}
            onClick={() => {
              lastFoused.current = null;
              onChange({
                target: {
                  name: "applicationBlacklist.applications",
                  value: valuesRef.current.applications.filter(
                    (app, i) => i !== rowNumber
                  )
                }
              });
            }}
          >
            {t({ defaultMessage: "移除" })}
          </Button>,
          td
        );
      }
    }
  ];

  const checked = useMemo(() => {
    const tmp = {};
    values.applications
      ?.filter(v => v.name)
      .forEach(app => {
        tmp[`${app.package_id}|||${app.version}|||${app.id}`] = app;
      });
    return tmp;
  }, [values.applications]);

  const handleAddByPackageId = () => {
    lastFoused.current = null;
    onChange({
      target: {
        name: "applicationBlacklist.applications",
        value: [
          {
            name: "",
            version: "",
            update_at: "",
            size: "",
            package_id: "",
            process_name: ""
          },
          ...values.applications
        ]
      }
    });
  };

  return (
    <Box flex={1}>
      <Typography variant="h5" style={{ marginBottom: 32 }}>
        {t({ defaultMessage: "黑名单" })}
      </Typography>
      <ConfigSwitch
        title={t({ defaultMessage: "启用" })}
        name="applicationBlacklist.open"
        onChange={(e) => {
          const v = e.target.checked
          setFieldValue("applicationBlacklist.open", v)
        }}
        marginBottom={3}
        value={values.open}
      />
      {values.open && (
        <>
          <Box marginTop={3} marginBottom={3}>
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: 118 }}
              onClick={() => setAddAppOpen(true)}
              disabled={readOnly}
            >
              {t({ defaultMessage: "添加应用" })}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: 118, marginLeft: 16 }}
              onClick={handleAddByPackageId}
              disabled={readOnly}
            >
              {t({ defaultMessage: "通过进程名添加" })}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: 118, marginLeft: 16 }}
              onClick={() => setUploadOpen(true)}
              disabled={readOnly}
            >
              {t({ defaultMessage: "本地上传应用" })}
            </Button>
          </Box>
          <DataTable
            options={{ serverSide: false }}
            data={values.applications}
            columns={columns}
            paging={false}
            showCheckAll={false}
            showFooter={false}
            autoLoad
          />
          <UploadWindowsAppDialog
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSuccess={data => {
              onChange({
                target: {
                  name: "applicationBlacklist.applications",
                  value: [
                    ...values.applications.filter(v => !v.name),
                    {
                      id: data.id,
                      name: data.package_name,
                      version: data.version,
                      upload_at: data.upload_at,
                      md5: data.md5,
                      size: data.size,
                      package_id: data.package_id,
                      process_name: data.package_id,
                      file_path: data.file_path
                    }
                  ]
                }
              });
            }}
          />
          {addAppOpen && (
            <AddAppModal
              noSub
              open={addAppOpen}
              onClose={() => setAddAppOpen(false)}
              enableSubVersion={false}
              checked={checked}
              onConfirm={result => {
                onChange({
                  target: {
                    name: "applicationBlacklist.applications",
                    value: [
                      ...result.map(data => ({
                        id: data.id,
                        name: data.name,
                        version: data.version,
                        upload_at: data.upload_at,
                        md5: data.md5,
                        size: data.size,
                        package_id: data.package_id,
                        process_name: data.process_name,
                        file_path: data.file_path
                      })),
                      ...values.applications.filter(v => !v.name)
                    ]
                  }
                });
                setAddAppOpen(false);
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export const initValues = {
  open: false,
  applications: []
};

export default AppBlockList;
